'use client';

/**
 * Header-Glocke: echte In-App-Benachrichtigungen (ersetzt die Metronic-Attrappe).
 *
 * Datenquelle: Tabelle `notifications`, geschrieben vom NotificationDispatcher bei
 * JEDER Benachrichtigung (auch wenn zusaetzlich Push/Mail/Telegram rausgeht).
 *   GET  /api/notifications?unread_only=&limit=&offset=
 *   GET  /api/notifications/unread/count     (Badge, Polling 60 s, nur sichtbarer Tab)
 *   POST /api/notifications/{id}/read
 *   POST /api/notifications/mark-all-read
 *
 * API der Komponente unveraendert: <NotificationsSheet trigger={<Button>…</Button>} />.
 * Den Ungelesen-Badge setzt die Komponente selbst ueber den Trigger.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  AlertCircle,
  Bell,
  BellOff,
  Calendar,
  CheckCheck,
  FolderKanban,
  LifeBuoy,
  Loader2,
  Mail,
  MessageSquare,
  Settings,
  ShoppingCart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const PAGE_SIZE = 30;
const POLL_MS = 60_000;

const TYPE_ICONS = {
  tickets: LifeBuoy,
  ticket: LifeBuoy,
  calendar: Calendar,
  messages: MessageSquare,
  message: MessageSquare,
  email: Mail,
  orders: ShoppingCart,
  projects: FolderKanban,
  project: FolderKanban,
  system: Settings,
};

/** "vor 5 Min." / "gestern" / "12.10.2026" — kompakt, deutschsprachig. */
function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('de', { numeric: 'auto', style: 'short' });
  if (diffSec < 45) return 'gerade eben';
  if (diffSec < 3600) return rtf.format(-Math.round(diffSec / 60), 'minute');
  if (diffSec < 86400) return rtf.format(-Math.round(diffSec / 3600), 'hour');
  if (diffSec < 7 * 86400) return rtf.format(-Math.round(diffSec / 86400), 'day');
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Ungelesen-Zaehler: pollt nur, solange der Tab sichtbar ist. */
function useUnreadCount() {
  const [count, setCount] = useState(0);
  const refresh = useCallback(async () => {
    if (!BackendAuth.getUser()) return;
    try {
      const res = await BackendAuth.apiCallJson('/api/notifications/unread/count');
      if (typeof res?.count === 'number') setCount(res.count);
    } catch {
      /* Badge ist nicht kritisch — still bleiben */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  return [count, setCount, refresh];
}

function NotificationRow({ item, onOpen, tx }) {
  const Icon = TYPE_ICONS[(item.type || '').toLowerCase()] || Bell;
  const unread = !item.read;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={cn(
          'group flex w-full items-start gap-3 px-5 py-3.5 text-start transition-colors',
          'hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-hidden',
          unread && 'bg-primary/[0.04]',
        )}
      >
        <span
          className={cn(
            'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border',
            unread
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-border bg-muted/50 text-muted-foreground',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-2">
            <span
              className={cn(
                'line-clamp-2 flex-1 text-sm text-foreground',
                unread ? 'font-semibold' : 'font-medium',
              )}
            >
              {item.title}
            </span>
            <span className="shrink-0 pt-0.5 text-[11px] text-muted-foreground" title={item.created_at ? new Date(item.created_at).toLocaleString('de-AT') : ''}>
              {formatWhen(item.created_at)}
            </span>
          </span>
          {item.message ? (
            <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{item.message}</span>
          ) : null}
        </span>
        {unread ? (
          <span className="mt-2 size-2 shrink-0 rounded-full bg-primary" aria-label={tx('unread', 'ungelesen')} />
        ) : (
          <span className="mt-2 size-2 shrink-0" aria-hidden="true" />
        )}
      </button>
    </li>
  );
}

export function NotificationsSheet({ trigger }) {
  const router = useRouter();
  const { t } = useTranslation();
  const tx = useCallback(
    (key, de, opts = {}) => t(`notifications_sheet.${key}`, { defaultValue: de, editable: false, ...opts }),
    [t],
  );

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount, refreshCount] = useUnreadCount();
  const reqRef = useRef(0);

  const load = useCallback(
    async ({ append = false } = {}) => {
      const reqId = ++reqRef.current;
      const offset = append ? items.length : 0;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await BackendAuth.apiCallJson(
          `/api/notifications?limit=${PAGE_SIZE}&offset=${offset}${filter === 'unread' ? '&unread_only=true' : ''}`,
        );
        if (reqId !== reqRef.current) return;
        const list = Array.isArray(res) ? res : [];
        setItems((prev) => (append ? [...prev, ...list] : list));
        setHasMore(list.length === PAGE_SIZE);
      } catch (e) {
        if (reqId !== reqRef.current) return;
        setError(e?.message || tx('load_error', 'Benachrichtigungen konnten nicht geladen werden.'));
      } finally {
        if (reqId === reqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [filter, items.length, tx],
  );

  // Beim Oeffnen und bei Filterwechsel neu laden.
  useEffect(() => {
    if (!open) return;
    load();
    refreshCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter]);

  const openItem = useCallback(
    async (item) => {
      if (!item.read) {
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
        setUnreadCount((c) => Math.max(0, c - 1));
        BackendAuth.apiCallJson(`/api/notifications/${item.id}/read`, { method: 'POST' }).catch(() => {});
      }
      const link = (item.link || '').trim();
      if (!link) return;
      try {
        const url = new URL(link, window.location.origin);
        setOpen(false);
        if (url.origin === window.location.origin) {
          router.push(`${url.pathname}${url.search}${url.hash}`);
        } else {
          window.open(url.toString(), '_blank', 'noopener');
        }
      } catch {
        /* ungueltiger Link — nur als gelesen markieren */
      }
    },
    [router, setUnreadCount],
  );

  const markAllRead = useCallback(async () => {
    setMarkingAll(true);
    try {
      await BackendAuth.apiCallJson('/api/notifications/mark-all-read', { method: 'POST' });
      setItems((prev) => (filter === 'unread' ? [] : prev.map((n) => ({ ...n, read: true }))));
      setUnreadCount(0);
    } catch (e) {
      setError(e?.message || tx('mark_error', 'Konnte nicht als gelesen markiert werden.'));
    } finally {
      setMarkingAll(false);
    }
  }, [filter, setUnreadCount, tx]);

  const badge = useMemo(() => {
    if (!unreadCount) return null;
    return (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground ring-2 ring-background"
      >
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    );
  }, [unreadCount]);

  const filters = [
    { key: 'all', label: tx('tab_all', 'Alle') },
    { key: 'unread', label: tx('tab_unread', 'Ungelesen'), count: unreadCount },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <span
          className="relative inline-flex"
          aria-label={
            unreadCount
              ? `${tx('title', 'Benachrichtigungen')} (${unreadCount} ${tx('unread', 'ungelesen')})`
              : tx('title', 'Benachrichtigungen')
          }
          title={tx('title', 'Benachrichtigungen')}
        >
          {trigger}
          {badge}
        </span>
      </SheetTrigger>
      <SheetContent className="inset-5 start-auto h-auto gap-0 rounded-lg p-0 sm:w-[460px] sm:max-w-none [&_[data-slot=sheet-close]]:end-5 [&_[data-slot=sheet-close]]:top-4.5">
        <SheetHeader className="mb-0 border-b border-border px-5 pb-0 pt-4">
          <SheetTitle className="pb-3 text-base">{tx('title', 'Benachrichtigungen')}</SheetTitle>
          <div className="-mb-px flex gap-4" role="tablist">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 border-b-2 pb-2.5 text-sm font-medium transition-colors',
                  filter === f.key
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
                {f.count ? (
                  <span className="rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary">
                    {f.count > 99 ? '99+' : f.count}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </SheetHeader>

        <SheetBody className="p-0">
          <ScrollArea className="h-[calc(100vh-11rem)]">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" role="status">
                <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {tx('loading', 'Wird geladen …')}
              </div>
            ) : error && items.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-16 text-center" role="alert">
                <AlertCircle className="size-6 text-destructive" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button variant="outline" size="sm" onClick={() => load()}>
                  {tx('retry', 'Erneut versuchen')}
                </Button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
                <BellOff className="size-7 text-muted-foreground/50" aria-hidden="true" />
                <p className="text-sm font-medium text-foreground">
                  {filter === 'unread'
                    ? tx('empty_unread', 'Alles gelesen')
                    : tx('empty', 'Keine Benachrichtigungen')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {tx('empty_hint', 'Neue Hinweise zu Tickets, Terminen und Vorgängen erscheinen hier.')}
                </p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-border">
                  {items.map((n) => (
                    <NotificationRow key={n.id} item={n} onOpen={openItem} tx={tx} />
                  ))}
                </ul>
                {hasMore && (
                  <div className="p-4 text-center">
                    <Button variant="ghost" size="sm" onClick={() => load({ append: true })} disabled={loadingMore}>
                      {loadingMore && <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                      {tx('load_more', 'Ältere laden')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </ScrollArea>
        </SheetBody>

        <SheetFooter className="border-t border-border p-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={markAllRead}
            disabled={markingAll || unreadCount === 0}
          >
            {markingAll ? (
              <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <CheckCheck className="size-4" aria-hidden="true" />
            )}
            {tx('mark_all_read', 'Alle als gelesen markieren')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default NotificationsSheet;
